/**
 * Maps status text for consistent display across the application
 */
export function mapStatusText(text: string | undefined): string | undefined {
  if (!text) return text;
  // Map "DID NOT REVIEW" and "Did Not Review" to "NOT ELIGIBLE"
  if (text === 'DID NOT REVIEW' || text === 'Did Not Review') {
    return 'NOT ELIGIBLE';
  }
  return text;
}

/**
 * Formats percentage values, replacing 0% with styled "NC"
 */
export function formatAdditionalElementsScore(score: string, reviewStatus: string): string {
  switch (reviewStatus) {
    case 'no-status':
      return `<span class="text-meta-grey">${score}</span>`;
    case 'meets':
      return `<span class="text-status-green">${score}</span>`;
    case 'partially-meets':
      return `<span class="text-status-orange">${score}</span>`;
    case 'does-not-meet':
      return `<span class="text-status-red">${score}</span>`;
    case 'not-claimed':
      return '<span class="text-meta-grey font-weight-bold">NC</span>';
    case 'not-eligible':
      return '<span class="text-meta-grey font-weight-bold">NE</span>';
    case 'narrative-only':
      return '<span class="text-meta-grey">Read</span>';
    default:
      return score;
  }
}

export function formatPercentageValue(value: string): string {
  if (value === '0%') {
    return '<span class="text-meta-grey font-weight-bold">NC</span>';
  }
  return value;
}

/**
 * Formats gateway scores for status cards, replacing 0% with appropriate abbreviation
 */
export function formatGatewayScore(
  score: string | undefined,
  reviewStatusSlug: string | undefined,
): string {
  if (!score) return 'NE';

  if (score === '0%') {
    // Determine abbreviation based on review status
    if (reviewStatusSlug === 'not-claimed') {
      return 'NC';
    } else if (
      reviewStatusSlug === 'not-eligible' ||
      reviewStatusSlug === 'did-not-review'
    ) {
      return 'NE';
    } else {
      // Default to NE for other cases with 0%
      return 'NE';
    }
  }

  return score;
}

/**
 * Check if a report has MultiLingual Learner (MLL) support
 * @param report - The report object to check
 * @returns boolean indicating MLL support availability
 */
export function hasMllSupport(report: any): boolean {
  if (!report) return false;

  // Check for MLL gateway by slug (more reliable than is_mll field)
  const hasMllGateway = report.gateways?.some(
    (gateway: any) =>
      gateway.slug === 'multilingual-learner-supports' ||
      gateway.is_mll === true,
  );

  const hasMllGatewayOverview = report.gateway_overviews?.some(
    (gateway: any) =>
      gateway.slug === 'multilingual-learner-supports' ||
      gateway.is_mll === true,
  );

  return hasMllGateway || hasMllGatewayOverview;
}

/**
 * Get the MLL gateway from a report's gateways array
 * @param report - The report object to check
 * @returns The MLL gateway object or null if not found
 */
export function getMllGateway(report: any): any | null {
  if (!report?.gateways) return null;

  return (
    report.gateways.find(
      (gateway: any) =>
        gateway.slug === 'multilingual-learner-supports' ||
        gateway.is_mll === true,
    ) || null
  );
}
